import { getBoxProgramColorTypeWithHex } from "@/modules/boxProgrammation/boxProgramUtils"
import { boxProgramColors, type BoxProgramColorType } from "@/modules/boxProgrammation/components/boxProgramColors"
import type {
  CoachCategoryDefaultSettingsFormFields,
  CoachCategoryInfoFormFields,
} from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import {
  CategoryProgramVisibility,
  type CategoryProgramBodyInputDto,
  type CategoryProgramDto,
  type CategorySlotDefaultInputDto,
  type CategorySlotDefaultValueDto,
} from "@modules/coachCategories/gateway/coachCategoriesDto"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import type { HUImage } from "@modules/types/imageTypes"

export class CoachCategoryEntity {
  id: string
  name: string
  description: string
  color: BoxProgramColorType
  visibleToClients: boolean
  offerIds: string[]
  image: HUImage | null
  defaultHideSessionContent: boolean
  subscriberSlotVisibility: boolean
  archived: boolean

  private constructor(data: Omit<CoachCategoryEntity, "toInfoFormValues">) {
    this.id = data.id
    this.name = data.name
    this.description = data.description
    this.color = data.color
    this.visibleToClients = data.visibleToClients
    this.offerIds = data.offerIds
    this.image = data.image
    this.defaultHideSessionContent = data.defaultHideSessionContent
    this.subscriberSlotVisibility = data.subscriberSlotVisibility
    this.archived = data.archived
  }

  static fromDto(dto: CategoryProgramDto): CoachCategoryEntity {
    return new CoachCategoryEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      color: getBoxProgramColorTypeWithHex(dto.color),
      visibleToClients: dto.visibility === CategoryProgramVisibility.COACH_MEMBERS,
      offerIds: dto.restrictions,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      subscriberSlotVisibility: dto.subscriberSlotVisibility,
      archived: dto.archived,
    })
  }

  toInfoFormValues(): CoachCategoryInfoFormFields {
    return {
      name: this.name,
      description: this.description,
      color: this.color,
      offerIds: this.offerIds,
      visibleToClients: this.visibleToClients,
    }
  }

  static fromFormValuesToProgramInputDto(
    info: CoachCategoryInfoFormFields,
    existingCategory?: CoachCategoryEntity,
  ): CategoryProgramBodyInputDto {
    return {
      name: info.name,
      description: info.description,
      imageKey: existingCategory?.image?.key ?? null,
      color: boxProgramColors[info.color].background,
      visibility: info.visibleToClients ? CategoryProgramVisibility.COACH_MEMBERS : CategoryProgramVisibility.PRIVATE,
      defaultHideSessionContent: existingCategory?.defaultHideSessionContent ?? false,
      subscriberSlotVisibility: existingCategory?.subscriberSlotVisibility ?? false,
      restrictions: info.offerIds,
    }
  }

  static toDefaultSettingsFormValues(dto: CategorySlotDefaultValueDto): CoachCategoryDefaultSettingsFormFields {
    return {
      duration: dto.duration ?? 60,
      participantLimit: dto.participantLimit,
      trialsAccepted: dto.trialsAccepted ?? true,
      limitStart: dto.participation?.limitStart ?? undefined,
      limitEnd: dto.participation?.limitEnd ?? undefined,
      limitCancel: dto.participation?.limitCancel ?? undefined,
      location: dto.location,
    }
  }

  static fromFormValuesToSlotDefaultsInputDto(
    settings: CoachCategoryDefaultSettingsFormFields,
  ): CategorySlotDefaultInputDto {
    return {
      duration: settings.duration,
      participantLimit: settings.participantLimit,
      location: settings.location,
      participation: {
        limitStart: settings.limitStart ?? null,
        limitEnd: settings.limitEnd ?? null,
        limitCancel: settings.limitCancel ?? null,
      },
      trialsAccepted: settings.trialsAccepted,
    }
  }
}
